import { calculateLocatinVars } from '../../../libs/browser';
import { parseError } from '../../../libs/parser';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CardComponent from '../../../components/CardComponent';
import CommonDescription from '../../../components/CommonDescription';
import CommonIconButton from '../../../components/CommonIconButton';
import Context from '../../../provider';
import IconBenefitsRed from '../../../assets/hkdc/icons/BenefitsRedIcon';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import LogoFooterComponent from '../../../components/LogoFooterComponent';
import React, { useContext, useEffect, useRef, useState } from 'react';
import memberApi from '../../../libs/api/memberApi';
import useIsScrolling from '../../../hooks/useIsScrolling';
import useNotificationsCount from '../../../hooks/useNotificationsCount';
import './index.scss';

export default function WebBenefits() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const locationVars = calculateLocatinVars(location.pathname);

  const notificationNotReadCount = useNotificationsCount();

  const scrollRef = useRef(null);
  const isScrolling = useIsScrolling(scrollRef.current);

  const [tierId, setTierId] = useState('');
  const [tierType, setTierType] = useState('');
  const [data, setData] = useState({});
  const [tierContentData, setTierContentData] = useState({});
  const [tierBenefitsData, setTierBenefitsData] = useState([]);
  const [activeTierPageCount, setActiveTierPageCount] = useState(1);

  const { init, dispatch, me, lang } = useContext(Context);

  const fetchData = async () => {
    try {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: true });
      const page = await memberApi.getTierBenefitsDetail({ id: tierId });
      setData(page.data);
    } catch (error) {
      dispatch({
        type: 'SET_ERROR_DIALOG',
        payload: { open: true, ...parseError(error) }
      });
    } finally {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: false });
    }
  };

  const fetchTierBenefitsData = async (page = 1) => {
    try {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: true });
      const response = await memberApi.getTierBenefits({
        page,
        pageSize: 10
      });
      setTierBenefitsData((prevData) => [...prevData, ...(response?.list ?? []).filter((d) => !(prevData.find((p) => p.uri_name === d.uri_name)))]);
    } catch (error) {
      dispatch({
        type: 'SET_ERROR_DIALOG',
        payload: { open: true, ...parseError(error) }
      });
    } finally {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: false });
    }
  };

  const fetchTierContentData = async () => {
    try {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: true });
      const page = await memberApi.getPage({ ruri: `${tierType}-benefits` });
      setTierContentData(page);
    } catch (error) {
      dispatch({
        type: 'SET_ERROR_DIALOG',
        payload: { open: true, ...parseError(error) }
      });
    } finally {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: false });
    }
  };

  // This page only allow logged in user
  useEffect(() => {
    if (init) {
      if (!locationVars[0]) {
        navigate(`/`);
      }
      fetchTierBenefitsData();
    }
  }, [init, me, navigate]);

  useEffect(() => {
    if (tierBenefitsData.length > 0) {
      // find tierId from tierBenefitsData list
      const tierBenefit = tierBenefitsData.find((item) => item.uri_name === `/tier-${locationVars[1]}`);
      if (!tierBenefit) navigate(`/`);
      setTierId(tierBenefit.id);
      setTierType(tierBenefit.uri_name.replace('/tier-', ''));
    }
  }, [tierBenefitsData]);

  useEffect(() => {
    if (tierId !== '') {
      fetchData();
    }
  }, [tierId]);

  useEffect(() => {
    if (tierType !== '') {
      fetchTierContentData();
    }
  }, [tierType]);

  const changeTier = (item) => {
    const tierType = item.uri_name.replace('/tier-', '');
    setTierId(item.id);
    setTierType(tierType);
    scrollRef.current?.scrollTo(0, 0);
  };

  return (
    (
      <div className='web-benefits-page'>
        <div ref={scrollRef} className="y-overflow gray-block content-container">
          <div className="benefit-page-title" style={{ backgroundImage: `url(${data.banner?.[lang]})` }}>
            <div className="section-max-container section-title">
              {/* <img src={data.banner?.[lang]} alt="benefit-page-title" /> */}
              <div className="title">
                {t(`tier-name.${tierType}`)}
              </div>
            </div>
          </div>

          <div className="section-max-container section">
            <div className="content-content-container">
              <div className="benefits-detail-container">
                <div className="bar-title-container">
                  <IconBenefitsRed
                    className="icon"
                    size="large" />
                  <div className="title">
                    {data.title?.[lang]}
                  </div>
                </div>
                <div className='list-cont'>
                  {
                    tierContentData.sections && tierContentData.sections.length > 0 && tierContentData.sections.map((item, index) => {
                      return (
                        <div key={index} className='item'>
                          <div className='item-img' style={{ backgroundImage: `url(${item.image[lang]})` }} />
                          <div className='item-content'>
                            <div className='item-text-title'>
                              {item.title[lang]}
                            </div>
                            <div className='item-text-desc'>
                              <CommonDescription data={item.content[lang]} />
                            </div>
                          </div>
                        </div>
                      );
                    })
                  }
                </div>
                <div className="desc-container">
                  <CommonDescription data={data?.content?.[lang]} />
                </div>
              </div>

              <div className="benefits-section-cont-bg">
                <div className='benefits-section-cont'>
                    <div className='benefits-title-cont'>
                      <IconBenefitsRed
                        className="icon"
                        size="large" />
                      <div className='title'>
                        {t('view-tier.title-2')}
                      </div>
                    </div>
                    <div className='benefits-cont'>
                      {
                        tierBenefitsData && tierBenefitsData.length > 0 &&
                        tierBenefitsData
                          .filter((item, index) => item.id !== tierId)
                          .filter((item, index) => (index + 1) - activeTierPageCount < 3 && (index + 1) - activeTierPageCount >= 0)
                          .map((item, index, arr) => {
                            return (
                              <CardComponent
                                key={item.uri_name}
                                cardBg={item.cover[lang]}
                                cardTitle={item.title[lang]}
                                cardDesc={item.subtitle[lang]}
                                cardImgClass={'tier-card-img'}
                                contentClass={'tier-card-content'}
                                btn={
                                  <CommonIconButton
                                    text= {t('home-before.card-btn-3')}
                                    alignItems = "unset"
                                    backgroundColor= "#000000"
                                    btnBackgroundColor= "#000000"
                                    btnColor = "#ffffff"
                                    width= "max-content"
                                    padding = "0.5rem 0.75rem"
                                    btnPadding="0rem"
                                    btnFontSize = "1rem"
                                    btnFontWeight = "700"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      changeTier(item);
                                    }}
                                  />
                                }
                              />
                            );
                          }
                        )
                      }
                    </div>
                </div>
                <div className="benefits-nation">
                  <div className="nation-btn">
                      <div className='nation-btn-previous'
                        style={{
                          cursor: activeTierPageCount !== 1 ? 'pointer' : 'default',
                          pointerEvents: activeTierPageCount !== 1 ? 'auto' : 'none'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (activeTierPageCount !== 1) {
                            setActiveTierPageCount(activeTierPageCount - 1);
                          }
                        }}
                      >
                        <KeyboardArrowLeftIcon
                          className="btn-icon"
                          style={{
                            visibility: activeTierPageCount !== 1 ? 'visible' : 'hidden'
                          }}
                          size="large"
                        />
                      </div>
                      <div className='nation-btn-next'
                        style={{
                          cursor: activeTierPageCount + 2 < tierBenefitsData.length - 1 ? 'pointer' : 'default',
                          pointerEvents: activeTierPageCount + 2 < tierBenefitsData.length - 1 ? 'auto' : 'none'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (activeTierPageCount + 2 < tierBenefitsData.length) {
                            setActiveTierPageCount(activeTierPageCount + 1);
                          }
                        }}
                      >
                        <KeyboardArrowRightIcon
                          className="btn-icon"
                          style={{
                            visibility: activeTierPageCount + 2 < tierBenefitsData.length - 1 ? 'visible' : 'hidden'
                          }}
                          size="large"
                        />
                      </div>
                    </div>
                </div>
              </div>
            </div>
          </div>

          <div className="black-block full-width">
            <LogoFooterComponent />
          </div>
        </div>
      </div>
    )
  );
}